import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { dismissWelcome } from '../api/users';
import { useAuthStore } from '../store/authStore';
import { useSignOut } from '../hooks/useSignOut';
import { formatLocalTime, isTodayInTz } from '../utils/time';
import { useSessionStore, type ActiveSession } from '../store/sessionStore';
import { BottomNav } from '../components/ui/BottomNav';
import { LanguagePicker } from '../components/ui/LanguagePicker';
import { StatusBadge } from '../components/ui/StatusBadge';
import { AvailabilityPill } from '../components/ui/AvailabilityPill';
import { CallButton } from '../components/ui/CallButton';
import { WhatsAppButton } from '../components/ui/WhatsAppButton';
import { AvailabilityChangeAlert } from '../components/AvailabilityChangeAlert';
import { LevelBanner } from '../components/LevelBanner';
import { useTranslation } from '../i18n';
import { DirectionalIcon } from '../components/ui/DirectionalIcon';
import { STALE_TIMES } from '../constants/staleTimes';
import SpotOwnerSetupWizard from '../components/SpotOwnerSetupWizard';

// ─── helpers ─────────────────────────────────────────────────────────────────

function fmtElapsed(s: number) {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return [h, m, sec].map((n) => String(n).padStart(2, '0')).join(':');
}

function elapsedFrom(startedAt: string) {
  return Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000));
}

// ─── sub-components ───────────────────────────────────────────────────────────

function SessionBanner({ session, hideButton = false, onEndClick, carouselHovered, onPrevClick, onNextClick, showArrows }: { session: ActiveSession; hideButton?: boolean; onEndClick?: () => void; carouselHovered?: boolean; onPrevClick?: () => void; onNextClick?: () => void; showArrows?: boolean }) {
  // Guard against incomplete session data
  const isScheduled = 'scheduled_start_at' in session;
  const startTime = isScheduled ? (session as any).scheduled_start_at : (session as any).started_at;
  if (!session?.spot?.level_label || !session?.spot?.spot_number || !session?.id || !startTime) {
    return null;
  }

  const timezone  = useAuthStore((s) => s.user?.building.timezone ?? 'UTC');
  const { t, direction, language } = useTranslation();
  const [elapsed, setElapsed] = useState(() => isScheduled ? 0 : elapsedFrom(startTime));

  useEffect(() => {
    if (isScheduled) return; // No timer for scheduled sessions
    const timer = setInterval(() => setElapsed(elapsedFrom(startTime)), 1000);
    return () => clearInterval(timer);
  }, [startTime, isScheduled]);

  const spotLabel = `${t(session.spot.level_code) || session.spot.level_label} · ${session.spot.spot_number}`;

  return (
    <div className="alert-session" style={{ margin: '0 0 8px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: '13px', color: 'var(--primary)', fontWeight: 600, marginBottom: '2px' }}>
            {isScheduled ? t('scheduled') : t('active_session')}
          </div>
          <div style={{ fontSize: '15px', fontWeight: 700 }}>{spotLabel}</div>
          {isScheduled ? (
            <div style={{ fontSize: '12px', color: 'var(--on-surface-2)', marginTop: '2px' }}>
              {t('scheduled_to_start')} {formatLocalTime(startTime, timezone, 'datetime', language)}
              {'available_until' in session && session.available_until && (
                <>
                  {' · '}
                  {t('scheduled_will_end')}{' '}
                  {/* Show day + month only when the end falls on a different
                      calendar date than the start (multi-day scheduled claim). */}
                  {new Date(session.available_until).toDateString() === new Date(startTime).toDateString()
                    ? formatLocalTime(session.available_until, timezone, 'time', language)
                    : formatLocalTime(session.available_until, timezone, 'datetime', language)}
                </>
              )}
            </div>
          ) : (
            'available_until' in session && session.available_until && (
              <div style={{ fontSize: '12px', color: 'var(--on-surface-2)', marginTop: '2px' }}>
                {t('available_until')}{' '}
                {/* If the session ends today (in the building TZ) show just the time;
                    otherwise show day + month so a multi-day claim reads correctly. */}
                {isTodayInTz(session.available_until, timezone)
                  ? formatLocalTime(session.available_until, timezone, 'time')
                  : formatLocalTime(session.available_until, timezone, 'datetime', language)}
              </div>
            )
          )}
        </div>
        <div style={{ textAlign: 'right' }}>
          {!isScheduled && (
            <div style={{ fontFamily: 'var(--manrope)', fontSize: '20px', fontWeight: 700, color: 'var(--primary)' }}>
              {fmtElapsed(elapsed)}
            </div>
          )}
        </div>
      </div>

      {!hideButton && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '5px',
            marginTop: '10px',
            flexDirection: direction === 'rtl' ? 'row-reverse' : 'row',
          }}
        >
          {/* Left arrow */}
          <button
            onClick={onPrevClick}
            disabled={!showArrows}
            style={{
              background: 'rgba(0, 0, 0, 0.1)',
              border: 'none',
              cursor: showArrows ? 'pointer' : 'default',
              color: '#ffffff',
              fontSize: '28px',
              padding: '4px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: showArrows ? (carouselHovered ? 1 : 0.5) : 0,
              transition: 'opacity 0.2s',
              width: '36px',
              height: '36px',
              pointerEvents: showArrows ? 'auto' : 'none',
            }}
            aria-label="Previous session"
          >
            <span className="material-symbols-outlined" style={{ fontSize: '28px' }}>chevron_left</span>
          </button>

          {/* End Session button (narrower) */}
          <SessionBannerButton session={session} onEndClick={onEndClick} />

          {/* Right arrow */}
          <button
            onClick={onNextClick}
            disabled={!showArrows}
            style={{
              background: 'rgba(0, 0, 0, 0.1)',
              border: 'none',
              cursor: showArrows ? 'pointer' : 'default',
              color: '#ffffff',
              fontSize: '28px',
              padding: '4px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: showArrows ? (carouselHovered ? 1 : 0.5) : 0,
              transition: 'opacity 0.2s',
              width: '36px',
              height: '36px',
              pointerEvents: showArrows ? 'auto' : 'none',
            }}
            aria-label="Next session"
          >
            <span className="material-symbols-outlined" style={{ fontSize: '28px' }}>chevron_right</span>
          </button>
        </div>
      )}
    </div>
  );
}

function SessionBannerButton({ session, onEndClick }: { session: ActiveSession; onEndClick?: () => void }) {
  const navigate  = useNavigate();
  const qc        = useQueryClient();
  const { t } = useTranslation();
  const isScheduled = 'scheduled_start_at' in session;

  const endMutation = useMutation({
    mutationFn: () => apiClient.patch(`/sessions/${session.id}/end`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['session', 'active'] });
      void qc.invalidateQueries({ queryKey: ['sessions', 'scheduled'] });
      // Force immediate refetch of free spots so cancelled spots reappear
      void qc.refetchQueries({ queryKey: ['spots', 'free'], type: 'all' });
      if (onEndClick) onEndClick();
    },
    onError: (error: any) => {
      // If session already ended, dismiss it from the UI
      if (error?.response?.data?.error?.code === 'SESSION_ALREADY_ENDED') {
        void qc.invalidateQueries({ queryKey: ['session', 'active'] });
        void qc.invalidateQueries({ queryKey: ['sessions', 'scheduled'] });
      }
    },
  });

  return (
    <button
      onClick={() => {
        if (endMutation.isPending) return;
        endMutation.mutate(undefined, { onSuccess: () => navigate('/') });
      }}
      disabled={endMutation.isPending}
      className={isScheduled ? 'btn-secondary' : 'btn-danger'}
      style={{ flex: 1, padding: '10px 20px', height: 'auto' }}
    >
      {endMutation.isPending ? (isScheduled ? t('cancelling') : t('ending')) : (isScheduled ? t('cancel_claim') : t('end_session'))}
    </button>
  );
}

function ReclaimBanner({ session }: { session: ActiveSession }) {
  const { reclaimAlert, clearReclaimAlert } = useSessionStore();
  const { t } = useTranslation();
  if (!reclaimAlert) return null;

  const ackMutation = useMutation({
    mutationFn: async () => {
      if (!reclaimAlert?.id) return null;
      return apiClient.patch(`/reclaim-alerts/${reclaimAlert.id}/acknowledge`).then((r) => r.data);
    },
    onSuccess: () => clearReclaimAlert(),
  });

  const spotLabel = `${session.spot.level_label} · ${session.spot.spot_number}`;

  return (
    <div className="alert-reclaim" style={{ margin: '0 0 8px' }}>
      <div style={{ fontSize: '13px', fontWeight: 700, color: '#ea580c', marginBottom: '4px' }}>
        {t('owner_needs_spot_back')}
      </div>
      <div className="body-md" style={{ marginBottom: '10px' }}>
        {t('Apt')} {session.spot.owner.apartment} {t('is_requesting')} {spotLabel}
      </div>
      <button
        onClick={() => ackMutation.mutate()}
        disabled={ackMutation.isPending}
        className="btn-primary"
        style={{ background: 'linear-gradient(135deg, #ea580c, #c2410c)' }}
      >
        {ackMutation.isPending ? t('sending') : t('ok_move_soon')}
      </button>
    </div>
  );
}

// ─── HomePage ─────────────────────────────────────────────────────────────────

interface MySpot {
  id: string;
  spot_number: string;
  level_label: string;
  level_code: string;
  status: 'free' | 'occupied' | 'unavailable';
  availability: { source: 'schedule' | 'window'; until: string } | null;
  active_session: {
    occupant_id: string;
    occupant_apartment: string;
    occupant_phone?: string;
  } | null;
}

export default function HomePage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const { activeSessions, reclaimAlert, setActiveSessions, setReclaimAlert } = useSessionStore();
  const timezone = useAuthStore((s) => s.user?.building.timezone ?? 'UTC');
  const isAdmin  = useAuthStore((s) => s.user?.is_admin ?? false);
  const signOut  = useSignOut();
  const { t, direction } = useTranslation();
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const welcomeDismissed = useRef(false);
  const [showOwnerWizard, setShowOwnerWizard] = useState(false);
  const [sessionIndex, setSessionIndex] = useState(0);
  const [carouselHovered, setCarouselHovered] = useState(false);
  const [showShareCard, setShowShareCard] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const qc = useQueryClient();

  const dismissMutation = useMutation({
    mutationFn: () => dismissWelcome(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['user', 'profile'] });
    },
  });

  // ── data ──────────────────────────────────────────────────────────────────
  // Profile fields (show_welcome, name, etc.) change rarely. WS doesn't push
  // profile changes, but the 5min refetch fallback is fine here.
  const userProfileQ = useQuery({
    queryKey: ['user', 'profile'],
    queryFn: () => apiClient.get<{ data: { show_welcome: boolean } }>('/users/me').then((r) => r.data.data),
    staleTime: STALE_TIMES.SLOW,
  });

  const sessionQ = useQuery({
    queryKey: ['session', 'active'],
    queryFn: () => apiClient.get<{ data: ActiveSession[] }>('/sessions/active').then((r) => r.data.data),
  });

  const scheduledQ = useQuery({
    queryKey: ['sessions', 'scheduled'],
    queryFn: () => apiClient.get<{ data: any[] }>('/sessions/scheduled').then((r) => r.data.data),
  });

  const mySpotsQ = useQuery({
    queryKey: ['spots', 'mine'],
    queryFn: () => apiClient.get<{ data: MySpot[] }>('/spots/mine').then((r) => r.data.data),
  });

  const freeSpotsQ = useQuery({
    queryKey: ['spots', 'free'],
    queryFn: () =>
      apiClient
        .get<{ data: { free_now: unknown[]; free_later_today: unknown[] } }>('/spots/free')
        .then((r) => r.data.data),
  });

  const incomingRequestsQ = useQuery({
    queryKey: ['spot-requests', 'incoming'],
    queryFn: () =>
      apiClient
        .get<{ data: any[] }>('/spot-requests/incoming')
        .then((r) => r.data.data),
  });

  const myRequestsQ = useQuery({
    queryKey: ['spot-requests', 'mine'],
    queryFn: () =>
      apiClient
        .get<{ data: any[] }>('/spot-requests')
        .then((r) => r.data.data),
  });

  useEffect(() => {
    if (userProfileQ.data?.show_welcome && !showWelcome && !welcomeDismissed.current) {
      setShowWelcome(true);
    }
  }, [userProfileQ.data?.show_welcome, showWelcome]);

  // Show owner setup wizard once for spot owners who haven't dismissed it.
  // Only triggers after the welcome popup is gone so modals don't stack.
  useEffect(() => {
    if (showWelcome) return;
    const dismissed = localStorage.getItem('spot_owner_wizard_dismissed');
    if (dismissed) return;
    const spots = mySpotsQ.data;
    if (spots && spots.length > 0 && !showOwnerWizard) {
      setShowOwnerWizard(true);
    }
  }, [mySpotsQ.data, showWelcome, showOwnerWizard]);

  useEffect(() => {
    if (sessionQ.data !== undefined) {
      // Combine active and scheduled sessions for carousel display
      const allSessions = [
        ...(sessionQ.data ?? []),
        ...(scheduledQ.data ?? []),
      ];
      setActiveSessions(allSessions);
      if (sessionQ.data.length > 0 && sessionQ.data[0]?.reclaim_alert) {
        setReclaimAlert(sessionQ.data[0].reclaim_alert);
      }
    }
  }, [sessionQ.data, scheduledQ.data, setActiveSessions, setReclaimAlert]);

  useEffect(() => {
    setSessionIndex(0);
  }, [activeSessions.length]);

  useEffect(() => {
    if (activeSessions.length > 0) {
      localStorage.setItem('lotngo_had_session', '1');
      localStorage.removeItem('lotngo_share_dismissed');
    } else if (localStorage.getItem('lotngo_had_session') === '1' && !localStorage.getItem('lotngo_share_dismissed')) {
      const sessionCount = Number(localStorage.getItem('lotngo_session_end_count') || '0') + 1;
      localStorage.setItem('lotngo_session_end_count', String(sessionCount));
      if (sessionCount === 1 || sessionCount === 4 || sessionCount === 10) {
        setShowShareCard(true);
      }
    }
  }, [activeSessions.length]);

  // ── derived ────────────────────────────────────────────────────────────────
  const currentSession      = activeSessions.length > 0 ? activeSessions[sessionIndex % activeSessions.length] : null;
  const allMySpots          = mySpotsQ.data ?? [];
  const mySpots             = allMySpots.filter((s) => s.status === 'occupied');
  const freeCount           = freeSpotsQ.data?.free_now.length ?? 0;
  const myActiveRequestsCount = (myRequestsQ.data ?? []).filter(
    (r: any) => r.status === 'pending' || r.status === 'accepted',
  ).length;
  // Sum of pending offers on the user's own pending requests. The list
  // endpoint joins spot_offers (status='pending') and returns offer_count
  // per request — see spotRequestService.listMyRequests. We surface this
  // as a separate "X new" pill so an unread offer doesn't get hidden by
  // the same count as an offer-less request.
  const newOffersCount = (myRequestsQ.data ?? []).reduce(
    (sum: number, r: any) => sum + (r.status === 'pending' ? Number(r.offer_count || 0) : 0),
    0,
  );
  const incomingRequestsCount = (incomingRequestsQ.data?.length ?? 0) + myActiveRequestsCount;
  const hasOccupiedSpots    = mySpots.length > 0;

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <div className="page" style={{ maxWidth: '430px', margin: '0 auto' }}>

      {/* ── Top bar ── */}
      <header className="topbar pt-[env(safe-area-inset-top,0px)] h-[calc(64px+env(safe-area-inset-top,0px))]" style={{
        position:          'sticky',
        top:               0,
        zIndex:            50,
        //height:            '64px',
        padding:           '0 24px',
        display:           'flex',
        justifyContent:    'space-between',
        alignItems:        'center',
        boxShadow:         '0 12px 32px -4px rgba(43,52,55,0.06)',
      }}>
        {/* Language picker on far left for LTR, far right (visual) for RTL */}
        <div style={{ width: '84px', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          <LanguagePicker />
        </div>
        <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none' }}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 114 26" style={{ height: '26px', width: 'auto', display: 'block' }} aria-label="Lot'N'Go">
            <text x="57" y="20" fontFamily="Manrope, system-ui, sans-serif" fontWeight="700" fontSize="20" fill="#005bc0" textAnchor="middle" letterSpacing="-0.4">{"Lot'N'Go"}</text>
          </svg>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', position: 'relative' }}>
          <button
            onClick={async () => {
              const shareData = {
                title: t('share_title'),
                text: t('share_text'),
                url: "https://www.lotngo.com",
              };
              try {
                if (navigator.share) {
                  await navigator.share(shareData);
                } else {
                  await navigator.clipboard.writeText(shareData.url);
                  alert(t('link_copied'));
                }
              } catch (err) {
                console.error('Share failed:', err);
              }
            }}
            style={iconBtn}
            title={t('share_button_title')}
            aria-label={t('share_button_title')}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '22px' }}>share</span>
          </button>
          <button
            onClick={() => navigate('/profile')}
            style={iconBtn}
            title={t('settings_button_title')}
            aria-label={t('settings_button_title')}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '22px' }}>settings</span>
          </button>
          <button
            onClick={() => setShowSignOutConfirm(true)}
            style={iconBtn}
            title={t('signout_button_title')}
            aria-label={t('signout_button_title')}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '22px' }}>logout</span>
          </button>
        </div>
      </header>

      {/* ── Scrollable body ── */}
      <div className="page-body">

        {/* Welcome popup */}
        {showWelcome && (
          <>
            <div style={s.welcomeBackdrop} onClick={() => {
              welcomeDismissed.current = true; setShowWelcome(false);
              dismissMutation.mutate();
            }} />
            <div style={s.welcomeModal}>
              <div style={s.welcomeIcon}>
                <span className="material-symbols-outlined" style={{ fontSize: 48, color: '#005bc0' }}>
                  celebration
                </span>
              </div>

              <h2 style={s.welcomeTitle}>
                {mySpotsQ.data && mySpotsQ.data.length > 0
                  ? t('welcome_greeting')
                  : t('welcome_to_lotngo')}
              </h2>

              <p style={s.welcomeText}>
                {mySpotsQ.data && mySpotsQ.data.length > 0
                  ? t('welcome_spots_assigned_desc')
                  : t('register_my_spot_desc')}
              </p>

              <div style={s.welcomeActions}>
                {mySpotsQ.data && mySpotsQ.data.length > 0 ? (
                  <button
                    onClick={() => {
                      welcomeDismissed.current = true; setShowWelcome(false);
                      dismissMutation.mutate();
                    }}
                    style={s.welcomePrimaryBtn}
                    disabled={dismissMutation.isPending}
                  >
                    {t('lets_go')}
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        welcomeDismissed.current = true; setShowWelcome(false);
                        dismissMutation.mutate();
                      }}
                      style={s.welcomeSecondaryBtn}
                      disabled={dismissMutation.isPending}
                    >
                      {t('dismiss')}
                    </button>
                    <button
                      onClick={() => {
                        welcomeDismissed.current = true; setShowWelcome(false);
                        dismissMutation.mutate(undefined, {
                          onSuccess: () => navigate('/spots/new'),
                        });
                      }}
                      style={s.welcomePrimaryBtn}
                      disabled={dismissMutation.isPending}
                    >
                      <span>{t('register_my_spot')}</span>
                      <DirectionalIcon icon="arrow_forward" style={{ fontSize: 20 }} />
                    </button>
                  </>
                )}
              </div>
            </div>
          </>
        )}

        {/* Spot owner setup wizard */}
        {showOwnerWizard && !showWelcome && allMySpots.length > 0 && (
          <SpotOwnerSetupWizard
            open
            spots={allMySpots.map((s) => ({
              id: s.id,
              spot_number: s.spot_number,
              level_label: s.level_label,
              level_code: s.level_code,
            }))}
            onDismiss={() => setShowOwnerWizard(false)}
          />
        )}

        {/* Alerts */}
        {currentSession && reclaimAlert && <ReclaimBanner session={currentSession} />}

        {/* Session carousel with button and arrows in horizontal row */}
        {currentSession && (
          <div
            onMouseEnter={() => setCarouselHovered(true)}
            onMouseLeave={() => setCarouselHovered(false)}
            onTouchStart={(e) => {
              touchStartX.current = e.touches[0].clientX;
            }}
            onTouchEnd={(e) => {
              if (touchStartX.current === null || activeSessions.length <= 1) return;
              const touchEndX = e.changedTouches[0].clientX;
              const diff = touchStartX.current - touchEndX;
              const threshold = 50;

              if (diff > threshold) {
                // Swiped left - go to next
                setSessionIndex((i) => (i + 1) % activeSessions.length);
              } else if (diff < -threshold) {
                // Swiped right - go to previous
                setSessionIndex((i) => (i - 1 + activeSessions.length) % activeSessions.length);
              }
              touchStartX.current = null;
            }}
          >
            <SessionBanner
              session={currentSession}
              hideButton={false}
              showArrows={activeSessions.length > 1}
              carouselHovered={carouselHovered}
              onPrevClick={() => setSessionIndex((i) => (i - 1 + activeSessions.length) % activeSessions.length)}
              onNextClick={() => setSessionIndex((i) => (i + 1) % activeSessions.length)}
            />
          </div>
        )}

        {/* ── Share card ── */}
        {showShareCard && !currentSession && (
          <div style={{
            background:    '#ffffff',
            borderRadius:  '16px',
            padding:       '20px',
            marginBottom:  '16px',
            boxShadow:     'var(--whisper)',
            border:        '1px solid rgba(171,179,183,0.1)',
            textAlign:     'center',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '8px' }}>
              <span className="material-symbols-outlined" style={{ fontSize: '22px', color: '#ea580c', fontVariationSettings: "'FILL' 1" }}>favorite</span>
              <span style={{ fontSize: '15px', fontWeight: 700, fontFamily: 'var(--manrope)', color: 'var(--on-surface)' }}>{t('share_card_title')}</span>
            </div>
            <p style={{ fontSize: '13px', color: 'var(--on-surface-2)', margin: '0 0 14px', lineHeight: 1.5 }}>
              {t('share_card_body')}
            </p>
            <button
              onClick={async () => {
                const shareData = {
                  title: t('share_title'),
                  text: t('share_text'),
                  url: 'https://www.lotngo.com',
                };
                try {
                  if (navigator.share) {
                    await navigator.share(shareData);
                  } else {
                    await navigator.clipboard.writeText(shareData.url);
                    alert(t('link_copied'));
                  }
                } catch (err) {
                  console.error('Share failed:', err);
                }
                localStorage.setItem('lotngo_share_dismissed', '1');
                setShowShareCard(false);
              }}
              style={{
                display:        'inline-flex',
                alignItems:     'center',
                gap:            '6px',
                background:     'linear-gradient(135deg, #005bc0, #004fa9)',
                color:          '#fff',
                border:         'none',
                borderRadius:   '10px',
                padding:        '10px 24px',
                fontSize:       '14px',
                fontWeight:     600,
                fontFamily:     'var(--inter)',
                cursor:         'pointer',
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>share</span>
              {t('share_now')}
            </button>
            <button
              onClick={() => {
                localStorage.setItem('lotngo_share_dismissed', '1');
                setShowShareCard(false);
              }}
              style={{
                display:    'block',
                margin:     '10px auto 0',
                background: 'none',
                border:     'none',
                fontSize:   '12px',
                color:      'var(--on-surface-3)',
                cursor:     'pointer',
              }}
            >
              {t('maybe_later')}
            </button>
          </div>
        )}

        <LevelBanner />

        {/* ── Bento stats grid ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '28px' }}>
          {/* Available spots */}
          <div
            onClick={() => navigate('/find')}
            style={{
              background:   '#ffffff',
              borderRadius: '24px',
              padding:      '14px',
              boxShadow:    'var(--whisper)',
              border:       '1px solid rgba(171,179,183,0.1)',
              cursor:       'pointer',
              display:      'flex',
              flexDirection:'column',
              justifyContent:'space-between',
              aspectRatio:  '1',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{
                width:          '27px',
                height:         '27px',
                borderRadius:   '9px',
                background:     'var(--primary-container)',
                display:        'flex',
                alignItems:     'center',
                justifyContent: 'center',
              }}>
                <span className="material-symbols-outlined" style={{ color: 'var(--primary)' }}>local_parking</span>
              </div>
              <span style={{
                fontSize:     '11px',
                fontWeight:   700,
                color:        'var(--primary)',
                padding:      '4px 8px',
                background:   'rgba(0,91,192,0.05)',
                borderRadius: '9999px',
              }}>
                {t('live')}
              </span>
            </div>
            <div>
              <div style={{ fontFamily: 'var(--manrope)', fontSize: '21px', fontWeight: 700, color: 'var(--on-surface)', marginBottom: '4px', lineHeight: 1 }}>
                {freeCount}
              </div>
              <div style={{ fontSize: '8px', fontWeight: 500, color: 'var(--on-surface-3)' }}>
                {t('available_spots')}
              </div>
            </div>
          </div>

          {/* My spots (occupied only) */}
          <div
            onClick={() => navigate('/spots')}
            style={{
              background:    '#f1f4f6',
              borderRadius:  '24px',
              padding:       '14px',
              border:        '1px solid rgba(171,179,183,0.1)',
              cursor:        'pointer',
              display:       'flex',
              flexDirection: 'column',
              justifyContent:'space-between',
              aspectRatio:   '1',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{
                width:          '27px',
                height:         '27px',
                borderRadius:   '9px',
                background:     '#e2e2e9',
                display:        'flex',
                alignItems:     'center',
                justifyContent: 'center',
              }}>
                <span className="material-symbols-outlined" style={{ color: '#5d5f65' }}>bookmark</span>
              </div>
            </div>
            <div>
              <div style={{ fontFamily: 'var(--manrope)', fontSize: '21px', fontWeight: 700, color: 'var(--on-surface)', marginBottom: '4px', lineHeight: 1 }}>
                {mySpots.length}
              </div>
              <div style={{ fontSize: '8px', fontWeight: 500, color: 'var(--on-surface-3)' }}>
                {t('active_parkings')}
              </div>
            </div>
          </div>

          {/* Spot requests */}
          <div
            onClick={() => navigate('/requests')}
            style={{
              background:   '#ffffff',
              borderRadius: '24px',
              padding:      '14px',
              boxShadow:    'var(--whisper)',
              border:       '1px solid rgba(171,179,183,0.1)',
              cursor:       'pointer',
              display:      'flex',
              flexDirection:'column',
              justifyContent:'space-between',
              aspectRatio:  '1',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{
                width:          '27px',
                height:         '27px',
                borderRadius:   '9px',
                background:     'rgba(168, 85, 247, 0.1)',
                display:        'flex',
                alignItems:     'center',
                justifyContent: 'center',
              }}>
                <span className="material-symbols-outlined" style={{ color: '#a855f7' }}>handshake</span>
              </div>
              {incomingRequestsCount > 0 && (
                <span style={{
                  fontSize:     '11px',
                  fontWeight:   700,
                  color:        '#fff',
                  padding:      '4px 8px',
                  background:   '#a855f7',
                  borderRadius: '9999px',
                }}>
                  {incomingRequestsCount}
                </span>
              )}
            </div>
            <div>
              <div style={{ fontFamily: 'var(--manrope)', fontSize: '21px', fontWeight: 700, color: 'var(--on-surface)', marginBottom: '4px', lineHeight: 1 }}>
                {incomingRequestsCount}
              </div>
              <div style={{ fontSize: '8px', fontWeight: 500, color: 'var(--on-surface-3)' }}>
                {t('spot_requests')}
              </div>
              {/* Distinct green pill when someone has offered on the user's
                  own request — the actionable signal that "you have a new
                  offer to review", visually separate from the raw request
                  count which doesn't change when offers arrive. */}
              {newOffersCount > 0 && (
                <div style={{
                  display:      'inline-block',
                  marginTop:    '4px',
                  fontSize:     '10px',
                  fontWeight:   700,
                  color:        '#fff',
                  padding:      '3px 7px',
                  background:   '#16a34a',
                  borderRadius: '9999px',
                }}>
                  {t('new_offers_pill').replace('{n}', String(newOffersCount))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Occupied spots section ── */}
        {hasOccupiedSpots && (
          <section style={{ marginBottom: '16px' }}>
            <div
              onClick={() => navigate('/spots')}
              className="section-head"
              style={{ cursor: 'pointer' }}
            >
              {t('active_parkings')}
            </div>
            {mySpots.slice(0, 2).map((spot) => (
              <div key={spot.id} className="card" style={{ cursor: 'auto' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }} onClick={() => navigate(`/spots/${spot.id}`)}>
                  <div style={{ flex: 1, cursor: 'pointer' }}>
                    <div style={{ fontWeight: 600, fontSize: '14px' }}>
                      {t(spot.level_code) || spot.level_label} · {spot.spot_number}
                    </div>
                    {spot.availability ? (
                      <div style={{ marginTop: '4px' }}>
                        <AvailabilityPill
                          source={spot.availability.source}
                          until={spot.availability.until}
                          timezone={timezone}
                        />
                      </div>
                    ) : (
                      <div className="body-md" style={{ marginTop: '4px' }}>
                        {t('no_availability_set')}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <StatusBadge status={spot.status} />
                    {spot.status === 'occupied' && spot.active_session?.occupant_phone && (
                      <>
                        <CallButton
                          occupantPhone={spot.active_session.occupant_phone}
                          occupantId={spot.active_session.occupant_id}
                          spotLabel={`${spot.level_label} · ${spot.spot_number}`}
                          variant="icon"
                        />
                        <WhatsAppButton
                          occupantPhone={spot.active_session.occupant_phone}
                          occupantId={spot.active_session.occupant_id}
                          spotLabel={`${spot.level_label} · ${spot.spot_number}`}
                          variant="icon"
                        />
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </section>
        )}

        {/* ── Availability-change negotiation banner (renders only when applicable) ── */}
        <AvailabilityChangeAlert />

        {/* ── Quick actions ── */}
        <section style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
            <h3 style={{ fontFamily: 'var(--manrope)', fontWeight: 700, fontSize: '20px', margin: 0 }}>
              {t('quick_actions')}
            </h3>
            <div style={{ flex: 1, height: '1px', background: '#e3e9ec', marginLeft: '8px' }} />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

          {/* Primary — Find a free spot */}
          <button
            onClick={() => navigate('/find')}
            style={{
              width:          '100%',
              padding:        '24px 28px',
              borderRadius:   '16px',
              border:         'none',
              background:     'linear-gradient(135deg, #005bc0 0%, #004fa9 100%)',
              cursor:         'pointer',
              display:        'flex',
              alignItems:     'center',
              justifyContent: 'space-between',
              boxShadow:      'var(--whisper)',
              transition:     'transform 0.15s',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{
                background:     'rgba(255,255,255,0.2)',
                padding:        '12px',
                borderRadius:   '12px',
                display:        'flex',
                alignItems:     'center',
                justifyContent: 'center',
                flexShrink:     0,
              }}>
                <span
                  className="material-symbols-outlined"
                  style={{ color: '#fff', fontVariationSettings: "'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24" }}
                >
                  search
                </span>
              </div>
              <div style={{ textAlign: 'start' }}>
                <span style={{ color: '#fff', fontFamily: 'var(--manrope)', fontWeight: 700, fontSize: '18px', display: 'block', lineHeight: 1.2 }}>
                  {t('find_a_free_spot')}
                </span>
                <span style={{ color: 'rgba(216,226,255,0.8)', fontSize: '13px' }}>
                  {t('locate_realtime_spaces')}
                </span>
              </div>
            </div>
            <DirectionalIcon icon="arrow_forward" style={{ color: '#fff' }} />
          </button>

            {/* Secondary — Request a spot */}
            <button
              onClick={() => navigate('/requests', { state: { openCreateForm: true } })}
              style={{
                width:          '100%',
                padding:        '24px 28px',
                borderRadius:   '16px',
                border:         'none',
                background:     '#1f2937',
                cursor:         'pointer',
                display:        'flex',
                alignItems:     'center',
                justifyContent: 'space-between',
                boxShadow:      'var(--whisper)',
                transition:     'transform 0.15s',
                marginBottom:   '4px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{
                  background:     'rgba(255,255,255,0.2)',
                  padding:        '12px',
                  borderRadius:   '12px',
                  display:        'flex',
                  alignItems:     'center',
                  justifyContent: 'center',
                  flexShrink:     0,
                }}>
                  <span
                    className="material-symbols-outlined"
                    style={{ color: '#fff', fontVariationSettings: "'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24" }}
                  >
                    add_circle
                  </span>
                </div>
                <div style={{ textAlign: 'start' }}>
                  <span style={{ color: '#fff', fontFamily: 'var(--manrope)', fontWeight: 700, fontSize: '18px', display: 'block', lineHeight: 1.2 }}>
                    {t('request_a_spot')}
                  </span>
                  <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>
                    {t('book_in_advance')}
                  </span>
                </div>
              </div>
              <DirectionalIcon icon="arrow_forward" style={{ color: '#fff' }} />
            </button>

            {/* Secondary — Mark my spot free */}
            {allMySpots.length > 0 && (
              <button
                onClick={() => allMySpots.length === 1 ? navigate(`/spots/${allMySpots[0].id}`) : navigate('/spots')}
                style={{
                  width:          '100%',
                  padding:        '24px 28px',
                  borderRadius:   '16px',
                  border:         'none',
                  background:     'linear-gradient(135deg, #f0f4f9 0%, #f8f9fa 100%)',
                  cursor:         'pointer',
                  display:        'flex',
                  alignItems:     'center',
                  justifyContent: 'space-between',
                  boxShadow:      'var(--whisper)',
                  transition:     'transform 0.15s',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{
                    background:     'rgba(0,91,192,0.1)',
                    padding:        '12px',
                    borderRadius:   '12px',
                    display:        'flex',
                    alignItems:     'center',
                    justifyContent: 'center',
                    flexShrink:     0,
                  }}>
                    <span
                      className="material-symbols-outlined"
                      style={{ color: 'var(--primary)', fontVariationSettings: "'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24" }}
                    >
                      location_on
                    </span>
                  </div>
                  <div style={{ textAlign: 'start' }}>
                    <span style={{ color: 'var(--on-surface)', fontFamily: 'var(--manrope)', fontWeight: 700, fontSize: '18px', display: 'block', lineHeight: 1.2 }}>
                      {t('mark_my_spot_free')}
                    </span>
                    <span style={{ color: 'var(--on-surface-3)', fontSize: '13px' }}>
                      {t('share_your_spot')}
                    </span>
                  </div>
                </div>
                <DirectionalIcon icon="arrow_forward" style={{ color: 'var(--on-surface-3)' }} />
              </button>
            )}

            {/* Register your spot prompt — only for users with no spots */}
            {allMySpots.length === 0 && (
              <button
                onClick={() => navigate('/spots/new')}
                style={{
                  width:          '100%',
                  padding:        '20px 24px',
                  borderRadius:   '16px',
                  border:         '1px solid var(--surface-low)',
                  background:     '#ffffff',
                  cursor:         'pointer',
                  display:        'flex',
                  alignItems:     'center',
                  gap:            '16px',
                  transition:     'transform 0.15s',
                }}
              >
                <div style={{
                  background:     'rgba(0,91,192,0.08)',
                  width:          48,
                  height:         48,
                  borderRadius:   '12px',
                  display:        'flex',
                  alignItems:     'center',
                  justifyContent: 'center',
                  flexShrink:     0,
                }}>
                  <span
                    className="material-symbols-outlined"
                    style={{ color: 'var(--primary)', fontSize: 24, fontVariationSettings: "'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24" }}
                  >
                    local_parking
                  </span>
                </div>
                <div style={{ flex: 1, textAlign: 'start' }}>
                  <span style={{ color: 'var(--on-surface)', fontFamily: 'var(--manrope)', fontWeight: 700, fontSize: '16px', display: 'block', lineHeight: 1.2 }}>
                    {t('have_a_spot')}
                  </span>
                  <span style={{ color: 'var(--on-surface-3)', fontSize: '13px' }}>
                    {t('register_and_share')}
                  </span>
                </div>
                <DirectionalIcon icon="arrow_forward" style={{ color: 'var(--on-surface-3)' }} />
              </button>
            )}

            {/* Admin panel — only for admins */}
            {isAdmin && (
              <div
                onClick={() => navigate('/admin')}
                style={{
                  background:   'linear-gradient(135deg, #1e3a5f 0%, #1e293b 100%)',
                  padding:      '14px',
                  borderRadius: '16px',
                  border:       'none',
                  display:      'flex',
                  alignItems:   'center',
                  gap:          '16px',
                  cursor:       'pointer',
                  boxShadow:    'var(--whisper)',
                }}
              >
                <div style={{
                  width:          '40px',
                  height:         '40px',
                  borderRadius:   '12px',
                  background:     'rgba(255,255,255,0.15)',
                  display:        'flex',
                  alignItems:     'center',
                  justifyContent: 'center',
                  flexShrink:     0,
                }}>
                  <span className="material-symbols-outlined" style={{ color: '#fff', fontSize: '20px', fontVariationSettings: "'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24" }}>
                    admin_panel_settings
                  </span>
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontWeight: 700, color: '#fff', fontSize: '15px', margin: 0 }}>
                    {t('admin_panel')}
                  </p>
                  <p style={{ fontSize: '12px', color: 'rgba(216,226,255,0.7)', margin: '2px 0 0' }}>
                    {t('manage_users_levels')}
                  </p>
                </div>
                <DirectionalIcon icon="chevron_right" style={{ color: 'rgba(255,255,255,0.5)' }} />
              </div>
            )}
          </div>
        </section>
      </div>

      <BottomNav />

      {/* ── Sign out confirm ── */}
      {showSignOutConfirm && (
        <>
          <div
            onClick={() => setShowSignOutConfirm(false)}
            style={{
              position:        'fixed',
              inset:           0,
              backgroundColor: 'rgba(0,0,0,0.4)',
              zIndex:          300,
            }}
          />
          <div style={{
            position:        'fixed',
            top:             '50%',
            left:            '50%',
            transform:       'translate(-50%, -50%)',
            width:           'calc(100% - 48px)',
            maxWidth:        '320px',
            backgroundColor: '#ffffff',
            borderRadius:    '20px',
            padding:         '24px 20px 16px',
            zIndex:          301,
            boxShadow:       '0 12px 32px -4px rgba(43,52,55,0.12)',
          }}>
            <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '6px' }}>
              {t('signout_confirm_title')}
            </div>
            <div className="body-md" style={{ marginBottom: '20px' }}>
              {t('signout_confirm_message')}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setShowSignOutConfirm(false)}
                className="btn-secondary"
                style={{ flex: 1, height: '44px' }}
              >
                {t('cancel')}
              </button>
              <button
                onClick={() => { setShowSignOutConfirm(false); void signOut(); }}
                className="btn-primary"
                style={{ flex: 1, height: '44px' }}
              >
                {t('signout')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── shared styles ────────────────────────────────────────────────────────────

const iconBtn: React.CSSProperties = {
  background:  'none',
  border:      'none',
  cursor:      'pointer',
  color:       'var(--on-surface-3)',
  padding:     '8px',
  display:     'flex',
  alignItems:  'center',
  fontSize:    '20px',
  lineHeight:  1,
};

const s: Record<string, React.CSSProperties> = {
  welcomeBackdrop: {
    position:        'fixed',
    inset:           0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex:          300,
    cursor:          'pointer',
  },
  welcomeModal: {
    position:        'fixed',
    top:             '50%',
    left:            '50%',
    transform:       'translate(-50%, -50%)',
    zIndex:          301,
    backgroundColor: '#ffffff',
    borderRadius:    '24px',
    padding:         '2.5rem 2rem',
    maxWidth:        '400px',
    width:           '90%',
    boxShadow:       '0 20px 64px rgba(0, 0, 0, 0.12)',
    display:         'flex',
    flexDirection:   'column' as const,
    alignItems:      'center',
    gap:             '1.5rem',
  },
  welcomeIcon: {
    width:           72,
    height:          72,
    borderRadius:    '50%',
    backgroundColor: 'var(--primary-container)',
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'center',
  },
  welcomeTitle: {
    margin:        '0',
    fontSize:      '1.75rem',
    fontWeight:    800,
    fontFamily:    'var(--manrope)',
    color:         'var(--on-surface)',
    textAlign:     'center' as const,
    letterSpacing: '-0.01em',
  },
  welcomeText: {
    margin:     '0',
    fontSize:   '0.95rem',
    color:      'var(--on-surface-2)',
    textAlign:  'center' as const,
    lineHeight: 1.6,
  },
  welcomeActions: {
    display:       'flex',
    gap:           '1rem',
    width:         '100%',
    marginTop:     '0.5rem',
  },
  welcomePrimaryBtn: {
    flex:           1,
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    gap:            '0.5rem',
    height:         48,
    borderRadius:   '12px',
    border:         'none',
    background:     'linear-gradient(135deg, #005bc0, #004fa9)',
    color:          'var(--on-primary)',
    fontSize:       '0.95rem',
    fontWeight:     700,
    fontFamily:     'var(--inter)',
    cursor:         'pointer',
    boxShadow:      '0 4px 12px rgba(0, 91, 192, 0.25)',
    transition:     'opacity 0.15s',
  },
  welcomeSecondaryBtn: {
    flex:           1,
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    height:         48,
    borderRadius:   '12px',
    border:         '1.5px solid var(--on-surface-3)',
    backgroundColor: 'transparent',
    color:          'var(--on-surface)',
    fontSize:       '0.95rem',
    fontWeight:     700,
    fontFamily:     'var(--inter)',
    cursor:         'pointer',
    transition:     'all 0.15s',
  },
};
